import { useState } from "react";
import { Outlet, NavLink, Link } from "react-router-dom";
import {
  LayoutDashboard, Users, Building2, Clock, Inbox, FileBarChart, CalendarRange, CalendarDays, CalendarClock, Network, Settings, LogOut, Menu, X, SlidersHorizontal, MapPin, FileText, Briefcase, Receipt, Palette, Megaphone, Route, Smartphone,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useEmpresa } from "@/hooks/useEmpresa";
import EmpresaBrand from "@/components/EmpresaBrand";
import VenturaLogo from "@/components/VenturaLogo";
import NotificationBell from "@/components/NotificationBell";
import ThemeToggle from "@/components/ThemeToggle";
import { ROL_LABEL } from "@/lib/format";
import { sectionGradientStyle, sectionIconInactiveStyle } from "@/lib/sectionTheme";

function navItems(rol, gestionarZonas = false, gestionarProyectos = false, gestionarGastos = false, gestionarRutas = false, permisosGerencia = null, gestionarGerencia = false) {
  if (rol === "super_admin") {
    return [
      { to: "/superadmin/empresas", label: "Empresas", icon: Building2, end: true, key: "empresas" },
      { to: "/superadmin/plantillas-colores", label: "Plantillas", icon: Palette, key: "plantillas" },
      { to: "/superadmin/configuracion", label: "Configuración", icon: SlidersHorizontal, key: "configuracion" },
      { to: "/superadmin/apk", label: "App Android", icon: Smartphone, key: "apk" },
    ];
  }
  const items = [
    { to: "/admin", label: "Panel", icon: LayoutDashboard, end: true, key: "panel" },
    { to: "/admin/planificacion", label: "Planificación", icon: CalendarRange, key: "planificacion" },
    { to: "/admin/vacaciones", label: "Vacaciones", icon: CalendarDays, key: "vacaciones" },
    { to: "/admin/organizacion", label: "Organización", icon: Network, key: "organizacion" },
    { to: "/admin/fichajes", label: "Fichajes", icon: Clock, key: "fichajes" },
    { to: "/admin/solicitudes", label: "Solicitudes", icon: Inbox, key: "solicitudes" },
    { to: "/admin/comunicados", label: "Comunicados", icon: Megaphone, key: "comunicados" },
    { to: "/admin/empleados", label: "Empleados", icon: Users, key: "empleados" },
    { to: "/admin/turnos", label: "Turnos y festivos", icon: CalendarClock, key: "turnos" },
    { to: "/admin/informes", label: "Listados e informes", icon: FileBarChart, key: "informes" },
    { to: "/admin/documentos", label: "Documentación", icon: FileText, key: "documentos" },
  ];
  if (gestionarProyectos) {
    items.push(
      { to: "/admin/proyectos", label: "Proyectos", icon: Briefcase, key: "proyectos" },
    );
  }
  if (gestionarGastos) {
    items.push(
      { to: "/admin/gastos", label: "Gastos", icon: Receipt, key: "gastos" },
      { to: "/mis-gastos", label: "Registrar gasto", icon: Receipt, key: "gastos" },
    );
  }
  if (gestionarRutas) {
    items.push(
      { to: "/admin/rutas-comerciales", label: "Rutas comerciales", icon: Route, key: "rutas" },
    );
  }
  if (gestionarZonas) {
    items.splice(2, 0, { to: "/admin/zonas", label: "Zonas de trabajo", icon: MapPin, key: "zonas" });
  }
  if (rol === "admin") items.push({ to: "/admin/ajustes", label: "Ajustes", icon: Settings, key: "ajustes" });
  if (rol === "admin" && gestionarGerencia) {
    // Insertar cerca de Organización
    const idxOrg = items.findIndex((it) => it.key === "organizacion");
    const itemGer = { to: "/admin/grupo-gerencia", label: "Grupo Gerencia", icon: Network, key: "gerencia" };
    if (idxOrg >= 0) items.splice(idxOrg + 1, 0, itemGer);
    else items.push(itemGer);
  }
  if (Array.isArray(permisosGerencia) && permisosGerencia.length && rol === "worker") {
    const allow = new Set(permisosGerencia);
    allow.delete("panel");
    return items.filter((it) => allow.has(it.key) && it.key !== "panel");
  }
  // Miembro Gerencia con rol manager: añade secciones Gerencia si faltan
  if (Array.isArray(permisosGerencia) && permisosGerencia.length && rol === "manager") {
    const allow = new Set(permisosGerencia);
    const keys = new Set(items.map((it) => it.key));
    const base = [
      { to: "/admin", label: "Panel", icon: LayoutDashboard, end: true, key: "panel" },
      { to: "/admin/vacaciones", label: "Vacaciones", icon: CalendarDays, key: "vacaciones" },
      { to: "/admin/fichajes", label: "Fichajes", icon: Clock, key: "fichajes" },
      { to: "/admin/solicitudes", label: "Solicitudes", icon: Inbox, key: "solicitudes" },
      { to: "/admin/gastos", label: "Gastos", icon: Receipt, key: "gastos" },
      { to: "/admin/rutas-comerciales", label: "Rutas comerciales", icon: Route, key: "rutas" },
      { to: "/admin/zonas", label: "Zonas de trabajo", icon: MapPin, key: "zonas" },
    ];
    for (const it of base) {
      if (allow.has(it.key) && !keys.has(it.key)) {
        items.push(it);
        keys.add(it.key);
      }
    }
  }
  return items;
}

function NavItem({ to, label, icon: Icon, end, styleKey, onNavigate }) {
  const key = styleKey || "panel";
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
      className={({ isActive }) =>
        `group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-all ${
          isActive
            ? "text-white font-medium shadow-md"
            : "text-zinc-600 hover:bg-white hover:shadow-sm dark:text-zinc-300 dark:hover:bg-zinc-800/80"
        }`
      }
      style={({ isActive }) => (isActive ? sectionGradientStyle(key) : undefined)}
    >
      {({ isActive }) => (
        <>
          <span
            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors ${isActive ? "bg-white/20" : ""}`}
            style={!isActive ? sectionIconInactiveStyle(key) : undefined}
          >
            <Icon className={`h-4 w-4 ${isActive ? "text-white" : ""}`} strokeWidth={1.5} />
          </span>
          {label}
        </>
      )}
    </NavLink>
  );
}

function Sidebar({ user, logout, onNavigate }) {
  const { empresa } = useEmpresa();
  const items = navItems(
    user.rol,
    empresa?.gestionar_zonas,
    empresa?.gestionar_proyectos,
    empresa?.gestionar_gastos,
    empresa?.gestionar_rutas_comerciales,
    user.es_aprobador_gerencia || user.es_miembro_gerencia ? user.permisos_menu_gerencia : null,
    empresa?.gestionar_gerencia,
  );

  return (
    <div className="flex h-full flex-col bg-gradient-to-b from-indigo-50/40 via-white to-violet-50/30 dark:from-zinc-950 dark:via-zinc-950 dark:to-zinc-900">
      <div className="border-b border-indigo-100/80 p-4 dark:border-zinc-800">
        {user.rol === "super_admin" ? (
          <div className="px-1 py-2">
            <VenturaLogo className="h-[1.875rem] w-auto max-w-full" />
            <p className="mt-1.5 text-[11px] text-indigo-600">{ROL_LABEL[user.rol]}</p>
          </div>
        ) : (
          <EmpresaBrand empresa={empresa} />
        )}
        {user.rol !== "super_admin" && (
          <p className="mt-2 px-1 text-[10px] font-semibold uppercase tracking-wider text-indigo-400">
            {ROL_LABEL[user.rol]}
          </p>
        )}
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
        {items.map(({ to, label, icon, end, key }) => (
          <NavItem key={to} to={to} label={label} icon={icon} end={end} styleKey={key} onNavigate={onNavigate} />
        ))}
      </nav>

      <div className="space-y-2 border-t border-indigo-100/80 bg-white/60 p-4 backdrop-blur-sm dark:border-zinc-800 dark:bg-zinc-900/60">
        {user.rol !== "super_admin" &&
          (user.rol !== "admin" || !empresa?.gestionar_gerencia || user.departamento_id) && (
          <Link
            to="/fichar"
            onClick={onNavigate}
            data-testid="nav-mi-fichaje"
            className="flex items-center gap-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 px-3 py-2.5 text-sm font-medium text-white shadow-sm transition-shadow hover:shadow-md"
          >
            <Clock className="h-4 w-4" strokeWidth={1.5} />
            Mi fichaje
          </Link>
        )}
        <div className="flex items-center justify-between rounded-xl border border-indigo-100 bg-white px-3 py-2.5 shadow-sm dark:border-zinc-700 dark:bg-zinc-900">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-zinc-950 dark:text-zinc-100">
              {user.nombre} {user.apellidos}
            </p>
            <p className="truncate text-[11px] text-zinc-400">{user.email}</p>
          </div>
          <div className="flex shrink-0 items-center gap-0.5">
            <ThemeToggle />
            <NotificationBell />
            <button
            onClick={logout}
            data-testid="admin-logout-btn"
            className="ml-2 shrink-0 rounded-lg p-1.5 text-zinc-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
            title="Cerrar sesión"
          >
            <LogOut className="h-4 w-4" strokeWidth={1.5} />
          </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { empresa } = useEmpresa();
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 border-r border-indigo-100/80 bg-white md:block dark:border-zinc-800 dark:bg-zinc-950">
        <Sidebar user={user} logout={logout} />
      </aside>

      <header className="sticky top-0 z-20 flex h-14 items-center justify-between border-b border-indigo-100 bg-white/90 px-4 backdrop-blur-xl dark:border-zinc-800 dark:bg-zinc-950/90 md:hidden">
        <EmpresaBrand empresa={empresa} compact />
        <div className="flex items-center gap-1">
          <ThemeToggle />
          <NotificationBell />
          <button
          onClick={() => setOpen(true)}
          data-testid="mobile-menu-btn"
          className="rounded-lg p-2 text-indigo-600 hover:bg-indigo-50"
        >
          <Menu className="h-5 w-5" strokeWidth={1.5} />
        </button>
        </div>
      </header>

      {open && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-zinc-950/30 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 bg-white shadow-2xl dark:bg-zinc-950">
            <button
              onClick={() => setOpen(false)}
              className="absolute right-3 top-3 z-10 rounded-lg p-2 text-zinc-400 hover:bg-zinc-100"
              data-testid="mobile-menu-close"
            >
              <X className="h-5 w-5" strokeWidth={1.5} />
            </button>
            <Sidebar user={user} logout={logout} onNavigate={() => setOpen(false)} />
          </div>
        </div>
      )}

      <main className="md:pl-64">
        <div className="mx-auto max-w-6xl p-4 md:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
